/**
 * Hook to handle OAuth deep link callbacks on native platforms
 *
 * On iOS/Android, when the OAuth flow completes, the browser redirects to
 * our Universal Link callback (for example, https://app.publicpulse.com/auth/callback?...).
 * This hook listens for that deep link and extracts the auth tokens
 * to complete the Supabase session.
 */

import { useEffect, useEffectEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useToast } from '@/hooks/use-toast';
import { isNativeOAuthCallbackUrl } from '@/lib/mobile/native-auth-launch';
import { isNativePlatform } from '@/lib/mobile/platform';

import { queueNativeOAuthCallback } from './native-oauth-callback';

import type { URLOpenListenerEvent } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';

export { NATIVE_OAUTH_FINISHED_EVENT } from './native-oauth-callback';
export const DESKTOP_OAUTH_CALLBACK_RECEIVED_EVENT = 'public-pulse:desktop-oauth-callback-received';

function removeAppUrlListener(listenerPromise: Promise<PluginListenerHandle>): void {
  listenerPromise.then(removeAppUrlListenerHandle).catch(() => null);
}

function removeAppUrlListenerHandle(listener: PluginListenerHandle): void {
  listener.remove().catch(() => null);
}

export function useDeepLinkAuth() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { toast } = useToast();

  const handleOAuthCallback = useEffectEvent(
    (url: string | null | undefined, shouldPublishUi: () => boolean) => {
      if (!url || !isNativeOAuthCallbackUrl(url)) {
        return;
      }

      queueNativeOAuthCallback({
        url,
        navigate,
        toast,
        shouldPublishUi,
        messages: {
          failedTitle: t('oauth.failedTitle'),
          failedDescription: t('oauth.failedDescription'),
          cancelledTitle: t('oauth.cancelledTitle'),
          cancelledDescription: t('oauth.cancelledDescription'),
        },
      });
    },
  );

  useEffect(() => {
    if (!isNativePlatform()) {
      return;
    }

    let cancelled = false;
    const shouldPublishUi = () => !cancelled;
    const handleAppUrlOpen = (event: URLOpenListenerEvent): void => {
      if (!cancelled) {
        handleOAuthCallback(event.url, shouldPublishUi);
      }
    };
    const handleLaunchUrl = (launchUrl: { url?: string } | undefined): void => {
      if (!cancelled) {
        handleOAuthCallback(launchUrl?.url, shouldPublishUi);
      }
    };

    const listenerPromise = import('@capacitor/app').then(async ({ App }) => {
      const pendingListener = App.addListener('appUrlOpen', handleAppUrlOpen);
      Promise.resolve()
        .then(async () => await App.getLaunchUrl())
        .then(handleLaunchUrl)
        .catch(() => null);
      return await pendingListener;
    });

    listenerPromise.catch(() => null);

    return () => {
      cancelled = true;
      removeAppUrlListener(listenerPromise);
    };
  }, []);
}
